import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Clock, TrendingUp, Calendar, Download, ChevronDown,
  FileText, ArrowRight, CreditCard, ExternalLink, AlertCircle, CheckCircle
} from 'lucide-react';
import { db, auth } from '../../config/firebase-config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const Earnings = () => {
  const navigate = useNavigate();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);

  const stripeData = (() => {
    try { return JSON.parse(localStorage.getItem('practitioner_stripe_connect') || 'null'); }
    catch { return null; }
  })();
  const stripeConnected = stripeData?.status === 'active';

  useEffect(() => {
    if (!db || !auth) { setLoading(false); return; }

    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setLoading(false); } }, 3000);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !done) {
        done = true;
        clearTimeout(timeout);
        try {
          // Mirrors Analytics.js / PractitionerDashboard.js pattern (no orderBy,
          // sort client-side) to avoid the composite-index requirement.
          const q = query(
            collection(db, 'bookings'),
            where('practitionerId', '==', user.uid)
          );
          const snap = await getDocs(q);
          const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          docs.sort((a, b) => {
            const aTime = a.dateTime?.toDate ? a.dateTime.toDate().getTime() : new Date(a.dateTime).getTime();
            const bTime = b.dateTime?.toDate ? b.dateTime.toDate().getTime() : new Date(b.dateTime).getTime();
            return bTime - aTime; // newest first
          });
          setTransactions(docs);
        } catch (err) {
          console.error('Earnings: Error loading transactions:', err);
        }
        setLoading(false);
      }
    });

    return () => { done = true; clearTimeout(timeout); unsubscribe(); };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-dropdown')) setShowExportMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const getBookingDate = (b) =>
    b.dateTime?.toDate ? b.dateTime.toDate() : new Date(b.dateTime);

  const totalEarnings = transactions.reduce((s, b) => s + (b.service?.price || 0), 0);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  const formatDate = (b) =>
    getBookingDate(b).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const getInitials = (name) =>
    (name || '').split(' ').map(n => n[0]).join('').toUpperCase();

  const generateCSV = (headers, rows, filename) => {
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = filename; link.click();
    window.URL.revokeObjectURL(url);
  };

  const generateTransactionCSV = () => {
    const today = new Date().toISOString().split('T')[0];
    generateCSV(
      ['Date', 'Client', 'Service', 'Amount', 'Status'],
      transactions.map(b => [
        formatDate(b),
        `${b.client?.firstName || ''} ${b.client?.lastName || ''}`.trim() || b.client?.email || 'Unknown',
        b.service?.name || 'Unknown',
        `$${b.service?.price || 0}`,
        (b.status || 'confirmed').charAt(0).toUpperCase() + (b.status || 'confirmed').slice(1)
      ]),
      `DF_Transactions_${today}.csv`
    );
    setShowExportMenu(false);
  };

  const generateYTDSummaryCSV = () => {
    const year = new Date().getFullYear();
    const ytd = transactions.filter(b => getBookingDate(b).getFullYear() === year);
    const total = ytd.reduce((s, b) => s + (b.service?.price || 0), 0);
    generateCSV(
      ['Period', 'Total Earnings', 'Total Sessions'],
      [[`${year} Year-to-Date`, `$${total}`, ytd.length.toString()]],
      `DF_YTD_Earnings_Summary_${year}.csv`
    );
    setShowExportMenu(false);
  };

  const recentTransactions = transactions.slice(0, 10);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-40" />
            <div className="h-8 bg-gray-200 rounded w-56" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-xl border border-gray-200 h-28" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <span
            onClick={() => navigate('/dashboard/practitioner')}
            className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/practitioner'); }}
          >
            Dashboard
          </span>
          <span className="text-gray-400" aria-hidden="true">/</span>
          <span className="text-gray-900 font-medium" aria-current="page">Earnings</span>
        </nav>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Earnings & Payouts</h1>
            <p className="text-sm text-gray-600">Track your income and bank deposits</p>
          </div>

          <div className="relative export-dropdown">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
              <ChevronDown className={`w-4 h-4 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <button
                  onClick={generateTransactionCSV}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-3"
                >
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900">Export Transaction History</span>
                </button>
                <button
                  onClick={generateYTDSummaryCSV}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-3"
                >
                  <TrendingUp className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900">Export Year-to-Date Summary</span>
                </button>
                <div className="border-t border-gray-200 my-2" />
                <button
                  onClick={() => { setShowExportMenu(false); navigate('/dashboard/reports'); }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <span className="text-sm text-purple-600 font-medium">View All Reports</span>
                  <ArrowRight className="w-4 h-4 text-purple-600" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-green-100 rounded-full p-3">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Total Earnings</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalEarnings)}</p>
            <p className="text-xs text-gray-500 mt-1">All time</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-amber-100 rounded-full p-3">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Pending Balance</p>
            <p className="text-2xl font-bold text-gray-900">—</p>
            <p className="text-xs text-gray-500 mt-1">Managed by Stripe</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-blue-100 rounded-full p-3">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Last Payout</p>
            <p className="text-2xl font-bold text-gray-900">—</p>
            <p className="text-xs text-gray-500 mt-1">See Stripe dashboard</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-purple-100 rounded-full p-3">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Total Sessions</p>
            <p className="text-2xl font-bold text-gray-900">{transactions.length}</p>
            <p className="text-xs text-gray-500 mt-1">All time</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Payout History - Takes 2 columns */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Payout History</h2>
              <p className="text-sm text-gray-600">Bank deposits processed by Stripe</p>
            </div>
            <div className="p-8 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Payout history is managed by Stripe. View your complete deposit history in the Stripe dashboard.
              </p>
              <a
                href="https://dashboard.stripe.com/payouts"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                View Payouts in Stripe
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Recent Transactions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-5 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">Recent Transactions</h2>
                <p className="text-sm text-gray-600">Individual payments from clients</p>
              </div>

              {recentTransactions.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-gray-500">No transactions yet — they'll appear here as clients book sessions.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {recentTransactions.map(tx => {
                    const clientName = `${tx.client?.firstName || ''} ${tx.client?.lastName || ''}`.trim()
                      || tx.client?.email || 'Unknown Client';
                    return (
                      <div key={tx.id} className="p-4 flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)', backgroundSize: '400% 400%', animation: 'auroraShift 16s ease infinite' }}
                        >
                          {getInitials(clientName)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{clientName}</p>
                          <p className="text-sm text-gray-500">
                            {tx.service?.name || 'Session'} • {formatDate(tx)}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-gray-900">{formatCurrency(tx.service?.price || 0)}</p>
                          <span className="text-xs font-semibold text-green-600 capitalize">
                            {tx.status || 'confirmed'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => navigate('/dashboard/clients')}
                  className="w-full text-center text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center justify-center gap-1"
                >
                  View All Clients
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-bold text-gray-900">Payment Methods</h2>
              </div>
              {stripeConnected ? (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Connected</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Stripe Connect active — clients can book and pay online.</p>
                  <a
                    href="https://dashboard.stripe.com"
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Manage in Stripe <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <span className="text-amber-700 font-medium">No payment method connected</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Connect Stripe to accept payments from clients.
                  </p>
                  <button
                    onClick={() => navigate('/dashboard/settings/business#stripe-connect')}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold text-sm hover:bg-purple-700 transition-colors"
                  >
                    Connect Stripe
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Earnings;
